/**
 * enemies.ts — word enemy movement, spawn, and rendering (Phase 41).
 *
 * Pure functions consumed by GameCanvas.tsx. No React, no DOM queries.
 * All per-frame constants multiplied by dt * 60 for frame-rate independence
 * (startGameLoop delivers dt in seconds; MAX_DELTA_TIME_SEC = 0.1).
 */

import type { Enemy, WordPoolEntry, SpawnEdge, IncomingIndicator } from './types';

// Locked constants (41-CONTEXT.md)
export const ENEMY_FONT = '14px "Courier New", monospace';
const PILL_BG = 'rgba(20, 20, 20, 0.9)';
const HIGHLIGHT = '#c084fc';
const URGENT = '#ef4444';
export const PILL_PAD_X = 10;
const PILL_PAD_Y = 6;
const PILL_RADIUS = 8;
const FONT_SIZE = 14;
const WOBBLE_AMPLITUDE = 15; // CSS px — perpendicular displacement amplitude
const WOBBLE_FREQUENCY = 0.05; // rad/frame @ 60fps
const EDGE_OFFSET = 50; // CSS px outside viewport

/**
 * Compute base enemy speed for a given wave and spawn category.
 * Formula: base = 1.3 + wave * 0.28; projects = base * 1.20; skills = base * 0.85.
 * Returns CSS px per frame at 60fps — multiply by dt * 60 in updateEnemy.
 */
export function computeSpeed(wave: number, category: Enemy['spawnCategory']): number {
  let base = 1.3 + wave * 0.28;
  if (category === 'projects') base *= 1.20;
  if (category === 'skills') base *= 0.85;
  return base; // px/frame @ 60fps
}

/**
 * Return a random spawn position exactly EDGE_OFFSET CSS px outside one of the 4 viewport edges,
 * plus edge metadata for the incoming-indicator system (hh7).
 *
 * edgeX/edgeY: the point ON the canvas border (no offset) where the enemy will cross.
 * x/y: the actual off-screen position (with EDGE_OFFSET) used for Enemy spawn.
 */
export function getSpawnPosition(cssW: number, cssH: number): {
  x: number; y: number; edge: SpawnEdge; edgeX: number; edgeY: number;
} {
  const edgeIdx = Math.floor(Math.random() * 4);
  switch (edgeIdx) {
    case 0: {                                          // top
      const rx = Math.random() * cssW;
      return { x: rx, y: -EDGE_OFFSET, edge: 'top', edgeX: rx, edgeY: 0 };
    }
    case 1: {                                          // right
      const ry = Math.random() * cssH;
      return { x: cssW + EDGE_OFFSET, y: ry, edge: 'right', edgeX: cssW, edgeY: ry };
    }
    case 2: {                                          // bottom
      const rx = Math.random() * cssW;
      return { x: rx, y: cssH + EDGE_OFFSET, edge: 'bottom', edgeX: rx, edgeY: cssH };
    }
    default: {                                         // left
      const ry = Math.random() * cssH;
      return { x: -EDGE_OFFSET, y: ry, edge: 'left', edgeX: 0, edgeY: ry };
    }
  }
}

/**
 * Create a new Enemy from a WordPoolEntry at a random off-screen edge position.
 */
export function spawnEnemy(entry: WordPoolEntry, cssW: number, cssH: number): Enemy {
  const pos = getSpawnPosition(cssW, cssH);
  return {
    word: entry.word,
    typed: 0,
    x: pos.x,
    y: pos.y,
    vx: 0,
    vy: 0,
    wobblePhase: 0,
    spawnCategory: entry.category,
    fontSize: entry.fontSize,
    trail: [],
  };
}

/**
 * Create a new Enemy at an explicit off-screen position (used by indicator-first spawn, hh7).
 * Coordinates come from a previously computed getSpawnPosition call stored in IncomingIndicator.
 */
export function spawnEnemyAt(entry: WordPoolEntry, x: number, y: number): Enemy {
  return {
    word: entry.word,
    typed: 0,
    x,
    y,
    vx: 0,
    vy: 0,
    wobblePhase: 0,
    spawnCategory: entry.category,
    fontSize: entry.fontSize,
    trail: [], // reserved for future tier-based enemy pattern effects
  };
}

/**
 * Render a pulsing directional arrow at the canvas edge for an incoming enemy (hh7).
 *
 * Arrow: filled triangle (8px) pointing INWARD from the edge.
 * Color: HIGHLIGHT purple with alpha pulsing 0.4–1.0 via sin(pulsePhase).
 * First letter of the word is drawn beside the arrow at the same alpha.
 * Wraps all ctx state in save()/restore().
 */
export function drawIncomingIndicator(
  ctx: CanvasRenderingContext2D,
  indicator: IncomingIndicator
): void {
  ctx.save();

  const alpha = 0.4 + 0.6 * (0.5 + 0.5 * Math.sin(indicator.pulsePhase));
  ctx.globalAlpha = alpha;
  ctx.fillStyle = HIGHLIGHT;
  ctx.font = ENEMY_FONT;
  ctx.textBaseline = 'middle';
  ctx.textAlign = 'left';

  const S = 8; // half-base of triangle in px
  const INSET = 12; // how far inward the arrow tip sits from the edge

  const { edgeX, edgeY, edge, word } = indicator;
  const firstLetter = word[0] ?? '';

  ctx.beginPath();

  switch (edge) {
    case 'top': {
      // Arrow points down (into canvas)
      ctx.moveTo(edgeX,     edgeY + INSET + S * 2); // tip
      ctx.lineTo(edgeX - S, edgeY + INSET);
      ctx.lineTo(edgeX + S, edgeY + INSET);
      ctx.fill();
      ctx.textAlign = 'center';
      ctx.fillText(firstLetter, edgeX, edgeY + INSET + S * 2 + 10);
      break;
    }
    case 'right': {
      // Arrow points left (into canvas)
      ctx.moveTo(edgeX - INSET - S * 2, edgeY); // tip
      ctx.lineTo(edgeX - INSET,         edgeY - S);
      ctx.lineTo(edgeX - INSET,         edgeY + S);
      ctx.fill();
      ctx.textAlign = 'right';
      ctx.fillText(firstLetter, edgeX - INSET - S * 2 - 6, edgeY);
      break;
    }
    case 'bottom': {
      // Arrow points up (into canvas)
      ctx.moveTo(edgeX,     edgeY - INSET - S * 2); // tip
      ctx.lineTo(edgeX - S, edgeY - INSET);
      ctx.lineTo(edgeX + S, edgeY - INSET);
      ctx.fill();
      ctx.textAlign = 'center';
      ctx.fillText(firstLetter, edgeX, edgeY - INSET - S * 2 - 4);
      break;
    }
    case 'left': {
      // Arrow points right (into canvas)
      ctx.moveTo(edgeX + INSET + S * 2, edgeY); // tip
      ctx.lineTo(edgeX + INSET,         edgeY - S);
      ctx.lineTo(edgeX + INSET,         edgeY + S);
      ctx.fill();
      ctx.textAlign = 'left';
      ctx.fillText(firstLetter, edgeX + INSET + S * 2 + 6, edgeY);
      break;
    }
  }

  ctx.restore();
}

/**
 * Advance an enemy one frame toward the cursor with sinusoidal wobble.
 *
 * Motion model:
 *   - Heading vector: normalized (enemy -> cursor)
 *   - Perpendicular vector: rotated 90 degrees from heading (for wobble)
 *   - Forward step: speed * dt * 60 px along heading
 *   - Lateral wobble: sin(wobblePhase) * WOBBLE_AMPLITUDE * 0.1 * dt * 60 px along perpendicular
 *
 * dt is in seconds (from startGameLoop). Multiplying by dt * 60 converts
 * px/frame constants to px/second-normalized values.
 *
 * Early-returns if distance to cursor < 1 px to prevent NaN from zero division.
 */
export function updateEnemy(
  enemy: Enemy,
  mouseX: number,
  mouseY: number,
  wave: number,
  dt: number
): void {
  const dx = mouseX - enemy.x;
  const dy = mouseY - enemy.y;
  const dist = Math.sqrt(dx * dx + dy * dy);
  if (dist < 1) return;

  const scale = dt * 60; // frame-rate independence factor
  const speed = computeSpeed(wave, enemy.spawnCategory);

  // Normalized heading vector
  const nx = dx / dist;
  const ny = dy / dist;

  // Perpendicular vector (90-degree CCW rotation)
  const px = -ny;
  const py = nx;

  // Advance wobble accumulator (frame-rate independent)
  enemy.wobblePhase += WOBBLE_FREQUENCY * scale;
  const wobble = Math.sin(enemy.wobblePhase) * WOBBLE_AMPLITUDE;

  // Store unscaled heading rate for debugging (multiply by dt*60 to get actual per-frame delta)
  enemy.vx = nx * speed + px * wobble * 0.1;
  enemy.vy = ny * speed + py * wobble * 0.1;

  // Advance position: forward step + sinusoidal lateral offset
  enemy.x += (nx * speed) * scale + px * wobble * 0.1 * scale;
  enemy.y += (ny * speed) * scale + py * wobble * 0.1 * scale;
}

/**
 * Measure cumulative x-offset of the character at charIndex in word.
 * PRECONDITION: ctx.font MUST equal ENEMY_FONT before calling.
 * Returns offset in CSS px relative to the start of the rendered text (not the pill).
 * Add pillX + PILL_PAD_X at the call site to get the absolute canvas x.
 *
 * Uses cumulative per-character measureText — the hybrid pattern (pretext provides
 * segment widths but not per-glyph x-positions; canvas measureText is the source
 * of truth for the actual draw engine).
 */
export function getCharXOffset(
  ctx: CanvasRenderingContext2D,
  word: string,
  charIndex: number
): number {
  let x = 0;
  for (let i = 0; i < charIndex; i++) {
    x += ctx.measureText(word[i]).width;
  }
  return x;
}

/**
 * Measure pill dimensions for the REMAINING (untyped) portion of an enemy word.
 * Pass the full word — typed index determines what's shown.
 * PRECONDITION: ctx.font MUST equal ENEMY_FONT before calling.
 */
export function measurePill(
  ctx: CanvasRenderingContext2D,
  word: string,
  typedCount: number = 0
): { pillW: number; pillH: number; textW: number } {
  const remaining = word.slice(typedCount);
  const textW = ctx.measureText(remaining).width;
  return {
    textW,
    pillW: textW + PILL_PAD_X * 2,
    pillH: FONT_SIZE + PILL_PAD_Y * 2,
  };
}

/**
 * Render a word enemy pill centered on enemy.x, enemy.y.
 *
 * Visual states:
 *   - Typed letters: dim rgba(150,150,150,0.4)
 *   - Next letter to type: HIGHLIGHT purple (#c084fc), or URGENT red (#ef4444) when <= 2 letters remain
 *   - Untyped letters: normal #e0e0e0
 *   - Urgent pill: red border added when remaining <= 2 and remaining > 0
 *
 * Wraps all ctx state in save()/restore().
 */
export function drawEnemy(ctx: CanvasRenderingContext2D, enemy: Enemy): void {
  ctx.save();
  ctx.font = ENEMY_FONT;
  ctx.textBaseline = 'top';
  ctx.textAlign = 'left';

  // Only render the remaining (untyped) portion of the word
  const remaining = enemy.word.length - enemy.typed;
  const isUrgent = remaining <= 2 && remaining > 0;

  const { pillW, pillH } = measurePill(ctx, enemy.word, enemy.typed);
  const pillX = enemy.x - pillW / 2;
  const pillY = enemy.y - pillH / 2;

  // Pill background
  ctx.fillStyle = PILL_BG;
  ctx.beginPath();
  if (typeof ctx.roundRect === 'function') {
    ctx.roundRect(pillX, pillY, pillW, pillH, PILL_RADIUS);
  } else {
    ctx.rect(pillX, pillY, pillW, pillH);
  }
  ctx.fill();

  // Urgent border
  if (isUrgent) {
    ctx.strokeStyle = URGENT;
    ctx.lineWidth = 1;
    ctx.stroke();
  }

  // Render only remaining (untyped) characters — typed letters have been "destroyed"
  const textY = pillY + PILL_PAD_Y;
  let cursorX = pillX + PILL_PAD_X;

  for (let i = enemy.typed; i < enemy.word.length; i++) {
    const char = enemy.word[i];
    const charW = ctx.measureText(char).width;

    if (i === enemy.typed) {
      ctx.fillStyle = isUrgent ? URGENT : HIGHLIGHT; // next = highlight
    } else {
      ctx.fillStyle = '#e0e0e0'; // untyped = normal
    }

    ctx.fillText(char, cursorX, textY);
    cursorX += charW;
  }

  ctx.restore();
}

/**
 * Draw a reduced-opacity ghost copy of an enemy pill.
 * Reserved for future tier-based enemy pattern effects (e.g. trails, echoes).
 * Not currently called — kept for when enemy pattern differentiation is implemented.
 */
export function drawGhostEnemy(
  ctx: CanvasRenderingContext2D,
  enemy: Enemy,
  alpha: number
): void {
  ctx.save();
  ctx.globalAlpha = alpha;
  ctx.font = ENEMY_FONT;
  ctx.textBaseline = 'top';
  ctx.textAlign = 'left';

  // Use measurePill to match exact pill dimensions from drawEnemy
  const { pillW, pillH } = measurePill(ctx, enemy.word, enemy.typed);
  const pillX = enemy.x - pillW / 2;
  const pillY = enemy.y - pillH / 2;

  // Pill background (same as drawEnemy)
  ctx.fillStyle = PILL_BG;
  ctx.beginPath();
  if (typeof ctx.roundRect === 'function') {
    ctx.roundRect(pillX, pillY, pillW, pillH, PILL_RADIUS);
  } else {
    ctx.rect(pillX, pillY, pillW, pillH);
  }
  ctx.fill();

  // Render remaining (untyped) characters in neutral grey — no highlight, no urgent color
  const textY = pillY + PILL_PAD_Y;
  let cursorX = pillX + PILL_PAD_X;
  ctx.fillStyle = '#cccccc';

  for (let i = enemy.typed; i < enemy.word.length; i++) {
    const char = enemy.word[i];
    const charW = ctx.measureText(char).width;
    ctx.fillText(char, cursorX, textY);
    cursorX += charW;
  }

  ctx.restore();
}
